/**
 * OrderService 통합 클래스
 * 모든 주문 관련 기능을 하나의 서비스로 제공
 */

// Import all order modules
import { 
  validateStockAvailability, 
  getCommissionRate, 
  isValidDeadlineDiscountTime 
} from './order-validation';
import { processPayment } from './order-payment';
import { 
  updateOrderStatus, 
  sendOrderNotification,
  updateOrderStatusWithFallback,
  cancelOrderInternal
} from './order-status';
import {
  getPendingOrderByCartId,
  cancelPendingOrder,
  createPendingOrderFromCart,
  confirmOrder
} from './order-pending';
import {
  cancelOrder,
  canCancelOrder,
  cleanupFailedPaymentOrders
} from './order-cancel';
import {
  getOrderByNumber,
  searchOrdersByNumber,
  getOrderStats
} from './order-search';
import {
  updatePickupTime,
  calculateExpectedPickupTime,
  getAvailablePickupTimes,
  validatePickupTime,
  getPickupTimeDescription,
  getPickupEndTime
} from './order-pickup';
import {
  getOrderById,
  getUserOrders,
  getRestaurantOrders,
  getRecentOrders,
  getActiveOrders,
  deleteOrder
} from './order-core';

/**
 * 주문 서비스 클래스
 */
class OrderService {
  // Core CRUD
  getOrderById = getOrderById;
  getUserOrders = getUserOrders;
  getRestaurantOrders = getRestaurantOrders;
  getRecentOrders = getRecentOrders;
  getActiveOrders = getActiveOrders;
  deleteOrder = deleteOrder;

  // Order Creation & Confirmation
  createPendingOrderFromCart = createPendingOrderFromCart;
  confirmOrder = confirmOrder;
  getPendingOrderByCartId = getPendingOrderByCartId;
  cancelPendingOrder = cancelPendingOrder;

  // Order Status
  updateOrderStatus = updateOrderStatus;
  sendOrderNotification = sendOrderNotification;
  updateOrderStatusWithFallback = updateOrderStatusWithFallback;
  cancelOrderInternal = cancelOrderInternal;

  // Order Cancellation
  cancelOrder = cancelOrder;
  canCancelOrder = canCancelOrder;
  cleanupFailedPaymentOrders = cleanupFailedPaymentOrders;

  // Payment
  processPayment = processPayment;

  // Search & Statistics
  getOrderByNumber = getOrderByNumber;
  searchOrdersByNumber = searchOrdersByNumber;
  getOrderStats = getOrderStats;

  // Pickup Management
  updatePickupTime = updatePickupTime;
  calculateExpectedPickupTime = calculateExpectedPickupTime;
  getAvailablePickupTimes = getAvailablePickupTimes;
  validatePickupTime = validatePickupTime;
  getPickupTimeDescription = getPickupTimeDescription;
  getPickupEndTime = getPickupEndTime;

  // Validation & Utilities
  validateStockAvailability = validateStockAvailability;
  getCommissionRate = getCommissionRate;
  isValidDeadlineDiscountTime = isValidDeadlineDiscountTime;
}

// Export singleton instance
export const orderService = new OrderService();

// Export types
export type { CreateOrderRequest } from './order-types';

// Export individual functions for backward compatibility
export {
  // Core CRUD
  getOrderById,
  getUserOrders,
  getRestaurantOrders,
  getRecentOrders,
  getActiveOrders,
  deleteOrder,

  // Order Creation & Confirmation
  createPendingOrderFromCart,
  confirmOrder,
  getPendingOrderByCartId,
  cancelPendingOrder,

  // Order Status
  updateOrderStatus,
  sendOrderNotification,
  updateOrderStatusWithFallback,
  cancelOrderInternal,

  // Order Cancellation
  cancelOrder,
  canCancelOrder,
  cleanupFailedPaymentOrders,

  // Payment
  processPayment,

  // Search & Statistics
  getOrderByNumber,
  searchOrdersByNumber,
  getOrderStats,

  // Pickup Management
  updatePickupTime,
  calculateExpectedPickupTime,
  getAvailablePickupTimes,
  validatePickupTime,
  getPickupTimeDescription,
  getPickupEndTime,

  // Validation & Utilities
  validateStockAvailability,
  getCommissionRate,
  isValidDeadlineDiscountTime
};